import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    InputAdornment,
    IconButton,
    Avatar,
    Stack,
    Snackbar,
    Alert
} from '@mui/material';
import { Visibility, VisibilityOff, PhotoCamera } from '@mui/icons-material';
import { UPDATE, UPDATE2 } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function PasswordResetDialog({ open, onClose }) {
    const { user } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(user?.avatarUrl || '');

    // Snackbar alert
    const [alert, setAlert] = useState({
        open: false,
        message: '',
        type: 'success',
    });

    useEffect(() => {
        if (!photoFile) return;
        const reader = new FileReader();
        reader.onload = (e) => setPhotoPreview(e.target.result);
        reader.readAsDataURL(photoFile);
    }, [photoFile]);

    const validatePassword = (pw) =>
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{6,10}$/.test(pw);

    const handleSubmit = async () => {
        setAlert({ open: false, message: '', type: 'success' });

        const isPasswordFilled = newPassword || confirmPassword;
        const isPhotoUpdated = !!photoFile;

        // 🔒 Handle password validation only if user is changing password
        if (isPasswordFilled) {
            if (!newPassword || !confirmPassword) {
                setAlert({ open: true, type: 'error', message: 'Both password fields are required.' });
                return;
            }

            if (!validatePassword(newPassword)) {
                setAlert({
                    open: true,
                    type: 'error',
                    message: 'Password must be 6–10 characters with upper, lower, number, special.',
                });
                return;
            }

            if (newPassword !== confirmPassword) {
                setAlert({ open: true, type: 'error', message: 'Passwords do not match.' });
                return;
            }
        }

        try {
            // 1. Upload profile image (if selected)
            if (isPhotoUpdated) {
                const formData = new FormData();
                formData.append('image', photoFile);

                console.log(photoFile);

                const imageResponse = await UPDATE2(`/users/profile-image/${user._id}`, formData);
                if (imageResponse?.status !== 200 && imageResponse?.status !== 201) {
                    setAlert({
                        open: true,
                        type: 'error',
                        message: imageResponse?.data?.message || 'Failed to upload profile image.',
                    });
                    return;
                }
            }

            // 2. Update password (only if requested)
            if (isPasswordFilled) {
                const passwordPayload = {
                    password1: newPassword,
                    password2: confirmPassword,
                };

                const passwordResponse = await UPDATE(`/users/password/${user._id}`, passwordPayload);
                if (passwordResponse?.status !== 200 && passwordResponse?.status !== 201) {
                    setAlert({
                        open: true,
                        type: 'error',
                        message: passwordResponse?.data?.message || 'Failed to update password.',
                    });
                    return;
                }
            }

            // ✅ Success if at least one update happened
            if (isPasswordFilled || isPhotoUpdated) {
                setAlert({
                    open: true,
                    type: 'success',
                    message: 'Profile updated successfully!',
                });

                setNewPassword('');
                setConfirmPassword('');
                setPhotoFile(null);

                setTimeout(() => {
                    onClose();
                }, 1500);
            } else {
                // No updates performed
                setAlert({
                    open: true,
                    type: 'info',
                    message: 'No changes made.',
                });
            }
        } catch (error) {
            console.error('Error:', error);
            setAlert({
                open: true,
                type: 'error',
                message: 'Something went wrong while updating profile.',
            });
        }
    };


    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
                <Box sx={{ backgroundColor: '#1e2a38', color: 'white', p: 0 }}>
                    <DialogTitle sx={{ m: 0, fontWeight: 'bold' }}>
                        Profile & Password
                    </DialogTitle>
                </Box>

                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={3}>
                        {/* Avatar */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Avatar
                                src={photoPreview}
                                sx={{ width: 64, height: 64, mx: 'auto', mb: 1 }}
                            />
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<PhotoCamera />}
                            >
                                Change Photo
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                        if (e.target.files[0]) setPhotoFile(e.target.files[0]);
                                    }}
                                />
                            </Button>
                        </Box>

                        {/* New Password */}
                        <TextField
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            size="small"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ mr: 1 }}
                                            onClick={() => setShowPassword((x) => !x)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Confirm Password */}
                        <TextField
                            label="Confirm Password"
                            type={showConfirm ? 'text' : 'password'}
                            fullWidth
                            size="small"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ mr: 1 }}
                                            onClick={() => setShowConfirm((x) => !x)}
                                            edge="end"
                                        >
                                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </DialogContent>

                <Box sx={{ backgroundColor: '#e3eaf2', p: 1 }}>
                    <DialogActions sx={{ p: 0, justifyContent: 'flex-end' }}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={handleSubmit}>Save</Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Snackbar Alert */}
            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    severity={alert.type}
                    variant="filled"
                    onClose={() => setAlert({ ...alert, open: false })}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </>
    );
}
